import React from 'react';
import { Input } from '@/components/ui/input';
import { ImagePlus, Loader2, Facebook, Instagram } from 'lucide-react';
import { useDashboard } from './dashboardContext';

export default function IdentityCard() {
  const { cfg, setCfg, upload, uploading } = useDashboard();
  return (
    <>
        {cfg?.template && (
          <div className="mb-8 p-5 rounded-2xl border border-white/10 bg-white/5" data-testid="business-display-name-card">
            <p className="font-semibold text-white text-sm sm:text-base">Uzņēmuma lapas identitāte</p>
            <p className="text-xs text-white/50 mt-0.5 mb-4">Nosaukums un logo parādās publiskajā uzņēmuma lapā un tavā publiskajā profilā. Ja atstāj tukšu, tiks izmantoti veikala dati.</p>
            <div className="flex items-start gap-5 flex-wrap">
              {/* Logo uploader */}
              <div className="flex flex-col items-center gap-2">
                <div className="w-20 h-20 rounded-2xl bg-white/5 border border-white/10 overflow-hidden flex items-center justify-center" data-testid="business-logo-preview">
                  {cfg?.display_logo_url
                    ? <img src={cfg.display_logo_url} alt="logo" className="w-full h-full object-contain bg-white/5" />
                    : <ImagePlus className="w-6 h-6 text-white/30" />}
                </div>
                <label className="cursor-pointer inline-flex items-center gap-1.5 text-xs font-medium text-[#D97736] hover:underline">
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files[0] && upload(e.target.files[0], (u) => setCfg((c) => ({ ...c, display_logo_url: u })))} data-testid="business-logo-upload" />
                  {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ImagePlus className="w-3.5 h-3.5" />}
                  {cfg?.display_logo_url ? 'Mainīt logo' : 'Augšupielādēt logo'}
                </label>
                {cfg?.display_logo_url && (
                  <button onClick={() => setCfg((c) => ({ ...c, display_logo_url: '' }))} className="text-xs text-white/40 underline" data-testid="business-logo-remove">Noņemt</button>
                )}
              </div>
              {/* Name */}
              <div className="flex-1 min-w-[220px] space-y-3">
                <div>
                  <label className="text-xs text-white/50 mb-1 block">Uzņēmuma lapas nosaukums</label>
                  <Input
                    value={cfg?.display_name || ''}
                    onChange={(e) => setCfg((c) => ({ ...c, display_name: e.target.value }))}
                    placeholder="Piem. Salons Marta"
                    className="bg-white/5 border-white/10 text-white"
                    data-testid="business-display-name-input"
                  />
                </div>
                <div>
                  <label className="text-xs text-white/50 mb-1 block">Šīs lapas adrese</label>
                  <Input
                    value={cfg?.address || ''}
                    onChange={(e) => setCfg((c) => ({ ...c, address: e.target.value }))}
                    placeholder="Piem. Brīvības iela 1, Rīga"
                    className="bg-white/5 border-white/10 text-white"
                    data-testid="business-page-address-input"
                  />
                  <p className="text-[11px] text-white/35 mt-1">Katrai lapai var būt sava adrese — tā tiks izmantota kartē (nākamā fāze).</p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div>
                    <label className="text-xs text-white/50 mb-1 block">Tālrunis</label>
                    <Input value={cfg?.contact_phone || ''} onChange={(e) => setCfg((c) => ({ ...c, contact_phone: e.target.value }))}
                      placeholder="+371 …" className="bg-white/5 border-white/10 text-white" data-testid="business-contact-phone-input" />
                  </div>
                  <div>
                    <label className="text-xs text-white/50 mb-1 block">E-pasts</label>
                    <Input value={cfg?.contact_email || ''} onChange={(e) => setCfg((c) => ({ ...c, contact_email: e.target.value }))}
                      placeholder="info@…" className="bg-white/5 border-white/10 text-white" data-testid="business-contact-email-input" />
                  </div>
                  <div>
                    <label className="text-xs text-white/50 mb-1 block">Vietne</label>
                    <Input value={cfg?.contact_website || ''} onChange={(e) => setCfg((c) => ({ ...c, contact_website: e.target.value }))}
                      placeholder="www.…" className="bg-white/5 border-white/10 text-white" data-testid="business-contact-website-input" />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-white/50 mb-1 block">Facebook</label>
                    <Input
                      value={cfg?.facebook || ''}
                      onChange={(e) => setCfg((c) => ({ ...c, facebook: e.target.value }))}
                      placeholder="facebook.com/tavslapa"
                      className="bg-white/5 border-white/10 text-white"
                      data-testid="business-facebook-input"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-white/50 mb-1 block">Instagram</label>
                    <Input
                      value={cfg?.instagram || ''}
                      onChange={(e) => setCfg((c) => ({ ...c, instagram: e.target.value }))}
                      placeholder="instagram.com/tavslapa"
                      className="bg-white/5 border-white/10 text-white"
                      data-testid="business-instagram-input"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-white/50 mb-1 block">TikTok</label>
                    <Input value={cfg?.tiktok || ''} onChange={(e) => setCfg((c) => ({ ...c, tiktok: e.target.value }))}
                      placeholder="tiktok.com/@tavslapa" className="bg-white/5 border-white/10 text-white" data-testid="business-tiktok-input" />
                  </div>
                  <div>
                    <label className="text-xs text-white/50 mb-1 block">YouTube</label>
                    <Input value={cfg?.youtube || ''} onChange={(e) => setCfg((c) => ({ ...c, youtube: e.target.value }))}
                      placeholder="youtube.com/@tavslapa" className="bg-white/5 border-white/10 text-white" data-testid="business-youtube-input" />
                  </div>
                  <div>
                    <label className="text-xs text-white/50 mb-1 block">WhatsApp</label>
                    <Input value={cfg?.whatsapp || ''} onChange={(e) => setCfg((c) => ({ ...c, whatsapp: e.target.value }))}
                      placeholder="+371 2xxxxxxx" className="bg-white/5 border-white/10 text-white" data-testid="business-whatsapp-input" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
    </>
  );
}
